import {
  ChangeDetectionStrategy,
  Component,
  afterNextRender,
  computed,
  signal,
} from '@angular/core';
import { HomeAiInsightsSectionComponent } from './components/home-ai-insights-section/home-ai-insights-section.component';
import { HomeBridgeSectionComponent } from './components/home-bridge-section/home-bridge-section.component';
import { HomeCaseStudySectionComponent } from './components/home-case-study-section/home-case-study-section.component';
import { HomeCtaSectionComponent } from './components/home-cta-section/home-cta-section.component';
import { HomeDeviceSectionComponent } from './components/home-device-section/home-device-section.component';
import { HomeEnterpriseSectionComponent } from './components/home-enterprise-section/home-enterprise-section.component';
import { HomeFeaturesSectionComponent } from './components/home-features-section/home-features-section.component';
import { HomeHeroSectionComponent } from './components/home-hero-section/home-hero-section.component';
import { HomeIntegrationsSectionComponent } from './components/home-integrations-section/home-integrations-section.component';

@Component({
  selector: 'app-home-page',
  imports: [
    HomeHeroSectionComponent,
    HomeBridgeSectionComponent,
    HomeCaseStudySectionComponent,
    HomeDeviceSectionComponent,
    HomeAiInsightsSectionComponent,
    HomeFeaturesSectionComponent,
    HomeEnterpriseSectionComponent,
    HomeIntegrationsSectionComponent,
    HomeCtaSectionComponent,
  ],
  templateUrl: './home-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:scroll)': 'onViewportChange()',
    '(window:resize)': 'onViewportChange()',
  },
  standalone: true,
})
export class HomePage {
  private readonly appPreviewSectionId = 'home-app-preview-section';
  private readonly minPreviewScale = 0.8;
  private readonly previewStartOffset = 0.9;
  private readonly previewEndOffset = 0.15;
  private readonly prefersReducedMotion = signal(false);
  private readonly previewProgress = signal(0);
  readonly appPreviewScale = computed(() =>
    this.prefersReducedMotion()
      ? 1
      : this.minPreviewScale + (1 - this.minPreviewScale) * this.previewProgress(),
  );

  constructor() {
    afterNextRender(() => this.updatePreviewProgress());
  }

  onViewportChange(): void {
    this.updatePreviewProgress();
  }

  private updatePreviewProgress(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.prefersReducedMotion.set(window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    if (this.prefersReducedMotion()) {
      this.previewProgress.set(1);
      return;
    }

    const previewElement = document.getElementById(this.appPreviewSectionId);
    if (!previewElement) {
      return;
    }

    const rect = previewElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 1;
    const start = viewportHeight * this.previewStartOffset;
    const end = viewportHeight * this.previewEndOffset;
    const rawProgress = (start - rect.top) / (start - end);

    this.previewProgress.set(this.clamp(rawProgress, 0, 1));
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }
}
